import { useState, useRef, useEffect, useCallback } from 'react';

interface TranscriptionConfig {
	input_audio_transcription: {
		model: string;
		language: string;
		prompt?: string;
	};
	turn_detection: {
		type: string;
		threshold?: number;
		prefix_padding_ms?: number;
		silence_duration_ms?: number;
	};
	id?: string;
}

interface Event {
	type: string;
	session?: TranscriptionConfig;
	transcript?: string;
	delta?: string;
	item_id?: string;
	content_index?: number;
	error?: any;
}

class RTCSession {
	useToken: boolean;
	stream: MediaStream | null;
	peer: RTCPeerConnection | null;
	channel: RTCDataChannel | null;
	muted: boolean;
	isDevMode: boolean;
	onconnectionstatechange?: (state: string) => void;
	onmessage?: (parsed: Event) => void;
	ontrack?: (e: RTCTrackEvent) => void;
	onopen?: () => void;
	onerror?: (e: Error) => void;

	constructor() {
		this.useToken = true;
		this.stream = null;
		this.peer = null;
		this.channel = null;
		this.muted = false;
		this.isDevMode = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';
	}

	private devLog(...args: any[]) {
		if (this.isDevMode) {
			console.log(...args);
		}
	}

	private devError(...args: any[]) {
		if (this.isDevMode) {
			console.error(...args);
		}
	}

	async startTranscription(
		audioStream: MediaStream,
		config: TranscriptionConfig
	): Promise<void> {
		await this.startInternal(
			audioStream,
			config,
			'/v1/realtime/transcription_sessions'
		);
	}

	stop(): void {
		if (this.channel) {
			this.channel.close();
			this.channel = null;
		}
		if (this.peer) {
			this.peer.close();
			this.peer = null;
		}
		if (this.stream) {
			this.stream.getTracks().forEach((track) => track.stop());
			this.stream = null;
		}
		this.muted = false;
	}

	async startInternal(
		audioStream: MediaStream,
		config: TranscriptionConfig,
		endpoint: string
	): Promise<void> {
		this.stream = audioStream;
		this.peer = new RTCPeerConnection();
		this.peer.ontrack = (event) => this.ontrack?.(event);
		this.peer.addTrack(audioStream.getTracks()[0]);
		this.peer.onconnectionstatechange = () => {
			if (this.peer && this.onconnectionstatechange) {
				this.onconnectionstatechange(this.peer.connectionState);
			}
		};
		this.channel = this.peer.createDataChannel('');
		this.channel.onopen = () => this.onopen?.();
		this.channel.onmessage = (event) => {
			if (this.onmessage) {
				try {
					const parsed = JSON.parse(event.data);
					this.onmessage(parsed);
				} catch (err) {
					this.devError('Error parsing message:', err);
				}
			}
		};
		const offer = await this.peer.createOffer();
		await this.peer.setLocalDescription(offer);
		try {
			const answer = await this.signal(offer, config, endpoint);
			await this.peer.setRemoteDescription(answer);
		} catch (error) {
			const errorObj =
				error instanceof Error ? error : new Error(String(error));
			this.onerror?.(errorObj);
		}
	}

	async signal(
		offer: RTCSessionDescriptionInit,
		config: TranscriptionConfig,
		endpoint: string
	): Promise<RTCSessionDescriptionInit> {
		let response: Response;

		if (this.useToken) {
			// Route session token request through proxy
			const sessionResponse = await fetch('/.netlify/functions/openai-realtime-proxy', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					path: endpoint,
					method: 'POST',
					headers: {
						'openai-beta': 'realtime-v1',
						'Content-Type': 'application/json',
					},
					body: config
				})
			});

			if (!sessionResponse.ok) {
				const errorText = await sessionResponse.text();
				throw new Error(`Failed to request session token: ${sessionResponse.statusText} - ${errorText}`);
			}

			const data = await sessionResponse.json();
			const secret = data.client_secret.value;

			this.devLog('Data received on first pass: ', data);

			// CHANGED: Use the realtime-preview model for WebRTC connection, but with transcription intent
			const realtimeEndpoint = '/v1/realtime?model=gpt-4o-realtime-preview&intent=transcription';

			// Route realtime connection through proxy
			response = await fetch('/.netlify/functions/openai-realtime-proxy', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					path: realtimeEndpoint,
					method: 'POST',
					headers: {
						'Authorization': `Bearer ${secret}`,
						'Content-Type': 'application/sdp',
					},
					body: offer.sdp
				})
			});

			if (!response.ok) {
				const errorText = await response.text();
				this.devError('Realtime connection failed:', response.status, errorText);
				throw new Error(`Failed to signal: ${response.statusText} - ${errorText}`);
			}
		} else {
			// CHANGED: Use the realtime-preview model for FormData fallback as well
			const realtimeEndpoint = '/v1/realtime?model=gpt-4o-realtime-preview&intent=transcription';

			// Route FormData fallback through proxy
			// Convert FormData to a format the proxy can handle
			const formDataObj = {
				session: JSON.stringify(config),
				sdp: offer.sdp || ''
			};

			response = await fetch('/.netlify/functions/openai-realtime-proxy', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					path: realtimeEndpoint,
					method: 'POST',
					headers: {
						'Content-Type': 'multipart/form-data'
					},
					body: formDataObj
				})
			});

			if (!response.ok) {
				const errorText = await response.text();
				this.devError('Realtime connection failed (FormData):', response.status, errorText);
				throw new Error(`Failed to signal: ${response.statusText} - ${errorText}`);
			}
		}

		const sdpResponse = await response.text();
		this.devLog('Received SDP response:', sdpResponse.substring(0, 100) + '...');

		// Check if the response is valid SDP
		if (!sdpResponse.includes('v=0') || !sdpResponse.includes('o=')) {
			this.devError('Invalid SDP response received:', sdpResponse);
			throw new Error('Invalid SDP response from server');
		}

		return { type: 'answer', sdp: sdpResponse };
	}
}

interface STTState {
	micAvailable: boolean;
	micPermissionAllowed: boolean;
	listening: boolean;
	transcribing: boolean;
	connecting: boolean;
	errorOccurred: boolean;
	errorMsg: string;
	transcript: string;
	start: () => Promise<void>;
	stop: () => void;
	clear: () => void;
}

const useSTT = (
	lang: string = 'en',
	prompt: string = ''
): STTState => {
	const [micAvailable, setMicAvailable] = useState<boolean>(true);
	const [micPermissionAllowed, setMicPermissionAllowed] = useState<boolean>(true);
	const [listening, setListening] = useState<boolean>(false);
	const [transcribing, setTranscribing] = useState<boolean>(false);
	const [connecting, setConnecting] = useState<boolean>(false);
	const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
	const [errorMsg, setErrorMsg] = useState<string>('');
	const [transcript, setTranscript] = useState<string>('');
	const sessionRef = useRef<RTCSession | null>(null);
	const processedRef = useRef<Set<string>>(new Set());

	// Feature flag for dev logging
	const isDevMode = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

	const devLog = (...args: any[]) => {
		if (isDevMode) {
			console.log(...args);
		}
	};

	const devError = (...args: any[]) => {
		if (isDevMode) {
			console.error(...args);
		}
	};

	const checkMic = async (): Promise<boolean> => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			setMicAvailable(true);
			setMicPermissionAllowed(true);
			stream.getTracks().forEach((track) => track.stop());
			return true;
		} catch (error: any) {
			setMicAvailable(true);
			if (
				error.name === 'NotAllowedError' ||
				error.name === 'PermissionDeniedError'
			) {
				setMicPermissionAllowed(false);
				setErrorMsg('Microphone permission denied.');
				setErrorOccurred(true);
			} else if (
				error.name === 'NotFoundError' ||
				error.name === 'DevicesNotFoundError'
			) {
				setMicAvailable(false);
				setErrorMsg('No microphone found.');
				setErrorOccurred(true);
			} else {
				setErrorMsg(`Error checking microphone: ${error.message}`);
				setErrorOccurred(true);
			}
			return false;
		}
	};

	const handleMsg = useCallback((event: Event): void => {
		devLog('Received WebRTC message:', event.type, event);

		switch (event.type) {
			case 'session.created':
				devLog('Session created, sending session update to disable responses');
				setConnecting(false);

				// CRITICAL: Send session update to disable AI responses
				if (sessionRef.current?.channel?.readyState === 'open') {
					const sessionUpdate = {
						type: 'session.update',
						session: {
							modalities: ['text'], // Only text, no audio output
							turn_detection: {
								type: 'server_vad',
								threshold: 0.3,
								prefix_padding_ms: 300,
								silence_duration_ms: 200,
								create_response: false // CRITICAL: Disable automatic responses
							},
							input_audio_transcription: {
								model: 'gpt-4o-transcribe',
								language: lang,
								prompt: prompt,
							}
						}
					};
					sessionRef.current.channel.send(JSON.stringify(sessionUpdate));
					devLog('Sent session update to disable responses');
				}
				break;

			case 'transcription_session.created':
				devLog('Transcription session created, stopping connecting state');
				setConnecting(false);
				break;

			case 'input_audio_buffer.speech_started':
				devLog('Speech started');
				setTranscribing(true);
				break;

			case 'input_audio_buffer.speech_stopped':
				devLog('Speech stopped');
				setTranscribing(false);
				break;

			// MAIN EVENT: This is where the actual transcription comes back
			case 'conversation.item.input_audio_transcription.completed':
				devLog('Transcription completed:', event.transcript);
				if (event.transcript) {
					const id = `${Date.now()}_${event.transcript}`;
					if (!processedRef.current.has(id)) {
						processedRef.current.add(id);
						const formatted = formatTranscript(event.transcript);
						setTranscript((prev) => prev + formatted);
					}
				}
				break;

			// OPTIONAL: Real-time transcription updates as you speak
			case 'conversation.item.input_audio_transcription.delta':
				devLog('Transcription delta:', event.delta);
				// Uncomment if you want live transcription updates:
				// if (event.delta) {
				//     setTranscript((prev) => prev + event.delta);
				// }
				break;

			// ERROR HANDLING: Transcription failures
			case 'conversation.item.input_audio_transcription.failed':
				devError('Transcription failed:', event.error);
				setErrorMsg('Transcription failed');
				setErrorOccurred(true);
				setTranscribing(false);
				break;

			case 'input_audio_buffer.committed':
				devLog('Audio buffer committed for transcription');
				setTranscribing(true); // Start showing transcribing state
				break;

			case 'session.updated':
				devLog('Session updated successfully:', event);
				break;

			case 'error':
				devError('WebRTC error event:', event);
				setErrorMsg('WebRTC error occurred');
				setErrorOccurred(true);
				break;

			// IGNORE: These are conversation/response events we don't want
			case 'response.created':
			case 'response.output_item.added':
			case 'response.content_part.added':
			case 'response.audio_transcript.delta':
			case 'response.audio.done':
			case 'response.audio_transcript.done':
				devLog('Ignoring conversation response event (these should stop after session update):', event.type);
				break;

			default:
				devLog('Unhandled message type:', event.type);
				break;
		}
	}, [lang, prompt, devLog, devError]);

	const formatTranscript = (text: string): string => {
		let formatted = text.trim();

		if (formatted.length > 0) {
			formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
		}

		formatted = formatted.replace(/\s+([,.!?:;])/g, '$1');

		formatted = formatted.replace(/([.!?:;])\s*([a-zA-Z])/g, '$1 $2');
		formatted = formatted.replace(/(,)\s*([a-zA-Z])/g, '$1 $2');

		formatted = formatted.replace(
			/([.!?])\s+([a-z])/g,
			(match, punct, letter) => {
				return punct + ' ' + letter.toUpperCase();
			}
		);

		return formatted.length > 0 ? ' ' + formatted : formatted;
	};

	const handleErr = useCallback((error: Error): void => {
		devError(error);
		setErrorMsg(error.message);
		setErrorOccurred(true);
		setListening(false);
		setConnecting(false);
		setTranscribing(false);
		if (sessionRef.current) {
			sessionRef.current.stop();
			sessionRef.current = null;
		}
	}, [devError]);

	const start = useCallback(async (): Promise<void> => {
		const available = await checkMic();
		if (!available) {
			return;
		}

		setListening(true);
		setConnecting(true);
		setErrorOccurred(false);
		setErrorMsg('');
		processedRef.current.clear();

		sessionRef.current = new RTCSession();
		sessionRef.current.onconnectionstatechange = (state) => {
			devLog('WebRTC connection state changed:', state);
			if (state === 'connected') {
				devLog('WebRTC connected successfully');
				setConnecting(false);
			} else if (state === 'disconnected' || state === 'failed') {
				devLog('WebRTC connection failed or disconnected:', state);
				setListening(false);
				setConnecting(false);
				setTranscribing(false);
				setErrorMsg(`Connection ${state}. Please try again.`);
				setErrorOccurred(true);
				sessionRef.current = null;
			}
		};
		sessionRef.current.onopen = () => {
			devLog('WebRTC data channel opened');
			setConnecting(false);
		};
		sessionRef.current.onmessage = handleMsg;
		sessionRef.current.onerror = handleErr;

		// UPDATED CONFIG: Proper transcription session configuration
		const config: TranscriptionConfig = {
			input_audio_transcription: {
				model: 'gpt-4o-transcribe', // Use transcription model
				language: lang,
				prompt: prompt,
			},
			turn_detection: {
				type: 'server_vad',
				threshold: 0.3,
				prefix_padding_ms: 300,
				silence_duration_ms: 200,
			},
		};

		try {
			const micStream = await navigator.mediaDevices.getUserMedia({
				audio: {
					echoCancellation: true,
					noiseSuppression: true,
					autoGainControl: true,
				},
			});
			await sessionRef.current.startTranscription(micStream, config);
		} catch (error: any) {
			handleErr(error);
		}
	}, [lang, prompt, handleMsg, handleErr, devLog]);

	const stop = useCallback((): void => {
		if (sessionRef.current) {
			sessionRef.current.stop();
			sessionRef.current = null;
		}
		setListening(false);
		setConnecting(false);
		setTranscribing(false);
	}, []);

	const clear = useCallback((): void => {
		setTranscript('');
		processedRef.current.clear();
	}, []);

	useEffect(() => {
		return () => {
			if (sessionRef.current) {
				sessionRef.current.stop();
				sessionRef.current = null;
			}
		};
	}, []);

	return {
		micAvailable,
		micPermissionAllowed,
		listening,
		transcribing,
		connecting,
		errorOccurred,
		errorMsg,
		transcript,
		start,
		stop,
		clear,
	};
};

export default useSTT;
